import { motion } from "framer-motion";
import { Reveal, Chapter, SectionHead } from "@/components/Reveal";
import {
  UserRound, Cpu, UsersRound, DatabaseZap, UserX, ScanFace, KeyRound, Lock, GitMerge,
} from "lucide-react";

const FLOW = [
  { icon: UserRound, name: "Contributor", desc: "Captures an observation", cls: "text-signal-cyan border-signal-cyan/30" },
  { icon: Cpu, name: "AI quality check", desc: "Detects anomalies and duplicates", cls: "text-signal-violet border-signal-violet/30" },
  { icon: UsersRound, name: "Verifier network", desc: "Independent nodes attest", cls: "text-signal-violet border-signal-violet/30" },
  { icon: GitMerge, name: "Consensus", desc: "Agreement across the network", cls: "text-mist border-white/20" },
  { icon: DatabaseZap, name: "Verified data", desc: "Trusted, structured output", cls: "text-signal-green border-signal-green/40" },
];

const PRIVACY = [
  { icon: UserX, title: "Identity & data separation", text: "Only necessary information is associated with observations." },
  { icon: ScanFace, title: "PII redaction", text: "Faces and license plates can be automatically redacted where applicable." },
  { icon: KeyRound, title: "Controlled access", text: "Verifiers and applications receive only the data their role requires." },
  { icon: Lock, title: "Encryption", text: "Data is protected during transmission and storage." },
];

const Connector = () => (
  <div className="hidden md:flex flex-1 items-center px-2" aria-hidden="true">
    <svg className="h-px w-full">
      <line x1="0" y1="0" x2="100%" y2="0" stroke="rgba(34,211,238,0.3)" strokeWidth="1" className="flow-line" />
    </svg>
  </div>
);

export const Trust = () => {
  return (
    <>
      <section id="trust" className="relative py-28 sm:py-40 bg-ink-950" data-testid="section-trust">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Chapter label="Verification" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <SectionHead title={"Trust the data,\nnot just the camera."} />
            </div>
            <Reveal delay={0.15} className="lg:col-span-5">
              <p className="text-lg leading-relaxed text-steel max-w-md">
                Every observation passes through AI quality checks and a
                distributed verifier network before it becomes verified
                intelligence.
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <div className="mt-20 flex flex-col md:flex-row items-stretch" data-testid="trust-flow">
              {FLOW.map((s, i) => (
                <div key={s.name} className="flex md:flex-1 flex-col md:flex-row items-center">
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                    className="flex md:flex-col items-center gap-4 md:gap-0 md:text-center py-5 md:py-0"
                    data-testid={`trust-node-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span className={`relative flex h-14 w-14 shrink-0 items-center justify-center rounded-full border bg-ink-950 ${s.cls}`}>
                      <s.icon className="h-5 w-5" />
                      {i === FLOW.length - 1 && (
                        <span className="absolute inset-0 rounded-full border border-signal-green/40 soft-ping" aria-hidden="true" />
                      )}
                    </span>
                    <div className="md:mt-5">
                      <div className="font-display text-[15px] font-semibold tracking-tight text-mist">{s.name}</div>
                      <div className="mt-1 text-[13px] text-steel">{s.desc}</div>
                    </div>
                  </motion.div>
                  {i < FLOW.length - 1 && <Connector />}
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.3}>
            <p className="mt-14 text-sm text-steel/80">
              Planned verification architecture - components roll out through the Pilot phase.
            </p>
          </Reveal>
        </div>
      </section>

      <section id="privacy" className="relative py-28 sm:py-40 bg-ink-900/40" data-testid="section-privacy">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Chapter label="Privacy" />
          <SectionHead
            title={"Intelligence without\nunnecessary exposure."}
            className="max-w-2xl"
          />
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-x-12">
            {PRIVACY.map((p, i) => (
              <Reveal key={p.title} delay={0.07 * i} y={14}>
                <div
                  className="flex items-start gap-5 py-7 border-t border-white/5"
                  data-testid={`privacy-${p.title.toLowerCase().replace(/[\s&]+/g, "-")}`}
                >
                  <p.icon className="h-6 w-6 text-signal-cyan shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display text-lg font-semibold tracking-tight text-mist">{p.title}</h3>
                    <p className="mt-1.5 text-[15px] leading-relaxed text-steel">{p.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};
